/**
 * Handwritten TanStack Query hooks wrapping the generated SDK.
 *
 * Pattern:
 *   - One query/mutation hook per endpoint.
 *   - `queryKey` is the hook's stable identifier. Mutations invalidate the
 *     keys whose data they make stale.
 *   - All hooks return the standard TanStack Query result, so consumers can
 *     destructure `{ data, isLoading, error, mutate, ... }` as usual.
 *
 * To add a hook for a new endpoint, follow the existing examples — copy a
 * useQuery wrapper for a GET, a useMutation wrapper for a POST/PUT/DELETE,
 * and call `queryClient.invalidateQueries` for any keys the mutation
 * affects.
 */

import {
  keepPreviousData,
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  acceptInvite,
  acceptPortalNda,
  addEngagementPerson,
  addPortalPerson,
  adminActivityActions,
  adminActivityAnalytics,
  adminAddWorkspaceMember,
  adminAssignStaff,
  adminCreateWorkspace,
  adminDeleteUser,
  adminGetCurrentNda,
  adminGetUser,
  adminGetWorkspace,
  adminListActivity,
  adminListUsers,
  adminListWorkspaces,
  adminPublishNda,
  adminSetUserStatus,
  adminUnassignStaff,
  adminUpdateUser,
  changePassword,
  confirmContributorMap,
  type ConfirmMapRequest,
  type CoverageRowUpdate,
  createClientInvite,
  type DraftFlowRun,
  endPortalPreview,
  type EngagementCoverage,
  type EngagementFileUpload,
  freezeIntake,
  getActivity,
  getArtifactVersion,
  getAuthProviders,
  getContributorMap,
  getDraftFlow,
  getDraftFlowRun,
  getEngagement,
  getEngagementCoverage,
  getEngagementDataRequest,
  getEngagementIntake,
  getEngagementState,
  getHealth,
  getIngestBackfill,
  getMe,
  getMeDetail,
  getPortalCoverage,
  getPortalDraftFlowRun,
  getPortalEngagementOverview,
  getPortalEngagementStage,
  getPortalNda,
  getPortalPreview,
  type IntakeDraftRequest,
  keepPortalFile,
  listClientInvites,
  listEngagementFiles,
  type ListEngagementFilesData,
  listEngagementPeople,
  listEngagements,
  listNdaAcceptances,
  listPortalFiles,
  listPortalPeople,
  login,
  logout,
  markPortalRowMissing,
  type PersonFields,
  type PortalDraftFlowRun,
  type PortalFile,
  releaseDraftFlowVersion,
  removeEngagementPerson,
  removePortalFile,
  removePortalPerson,
  replacePortalFile,
  requestPasswordReset,
  resendClientInvite,
  resetPassword,
  runIngestBackfill,
  saveContributorMapVersion,
  type SaveDataRequestRequest,
  saveDataRequestVersion,
  saveDraftFlowVersion,
  type SaveFlowVersionRequest,
  saveIntakeDraft,
  setEngagementProcessCategory,
  setShelfContextStatus,
  type ShelfContextStatusUpdate,
  type StaffPersonFields,
  startDraftFlowRun,
  startPortalDraftFlowRun,
  startPortalPreview,
  updateCoverageRow,
  updateEngagementPerson,
  updatePortalPerson,
  updateProfile,
  uploadEngagementFile,
  uploadPortalFile,
} from "@/lib/api/generated";
import { logger } from "@/lib/logger";
import { isStaffRole, type Role } from "@/lib/roles";

import { apiErrorDetail, apiErrorRefusal, apiErrorStatus } from "./client";

// ─── Public queries / mutations ──────────────────────────────────────────

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: async () => {
      const { data } = await getHealth();
      return data;
    },
  });
}

interface UseMeOptions {
  /** Refetch whenever this observer mounts, even while the cached profile is fresh. */
  revalidateOnMount?: boolean;
  /** Refetch a stale profile when the window regains focus. */
  revalidateOnFocus?: boolean;
}

/**
 * The signed-in user's profile, or `null` once the backend says there is no
 * usable session (401: none, expired or revoked; 403: the account is
 * deactivated). Guards redirect on `!me`; any other failure (a 502, the network)
 * stays an error and leaves the last known profile in place.
 */
export function useMe({ revalidateOnMount = false, revalidateOnFocus = false }: UseMeOptions = {}) {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        const { data } = await getMe();
        return data ?? null;
      } catch (error) {
        const status = apiErrorStatus(error);
        if (status === 401 || status === 403) {
          logger.debug("[auth] no usable session:", status, apiErrorDetail(error));
          return null;
        }
        throw error;
      }
    },
    retry: false,
    ...(revalidateOnMount && { refetchOnMount: "always" as const }),
    ...(revalidateOnFocus && { refetchOnWindowFocus: true }),
  });
}

/** The external sign-in providers the backend can start right now (`google`). */
export function useAuthProviders() {
  return useQuery({
    queryKey: ["auth", "providers"],
    queryFn: async () => {
      const { data } = await getAuthProviders();
      return data;
    },
    // The backend caches the list for 5 minutes, so a sooner refetch learns
    // nothing. A failure only hides the Google button, so it isn't retried.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string; password: string; rememberMe?: boolean }) => {
      const { data } = await login({
        body: {
          email: vars.email,
          password: vars.password,
          remember_me: vars.rememberMe ?? false,
        },
      });
      return data;
    },
    onSuccess: () => {
      // A login can follow a session that ended WITHOUT the in-app logout
      // (expired/cleared cookie), so the cache may still hold the previous
      // identity's data — and a different user logging in on the same tab
      // would briefly see it. Clear everything, mirroring useLogout; the new
      // session refetches what it needs (including "me").
      queryClient.clear();
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await logout();
    },
    onSuccess: () => {
      // Clear the entire cache so no stale per-user data leaks across sessions.
      queryClient.clear();
    },
  });
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: async (vars: { email: string }) => {
      const { data } = await requestPasswordReset({ body: { email: vars.email } });
      return data;
    },
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: async (vars: { tokenHash: string; newPassword: string }) => {
      const { data } = await resetPassword({
        body: { token_hash: vars.tokenHash, new_password: vars.newPassword },
      });
      return data;
    },
  });
}

/**
 * Accept an invite: the backend spends the emailed link, adds the client to the
 * engagement's workspace, sets the password and signs them in (session cookie).
 */
export function useAcceptInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { tokenHash: string; password: string }) => {
      const { data } = await acceptInvite({
        body: { token_hash: vars.tokenHash, password: vars.password },
      });
      return data;
    },
    onSuccess: () => {
      // A new identity signs in on this tab: drop whatever the cache held, as useLogin does.
      queryClient.clear();
    },
  });
}

// ─── Profile ─────────────────────────────────────────────────────────────

export function useMeDetail() {
  return useQuery({
    queryKey: ["me", "detail"],
    queryFn: async () => {
      const { data } = await getMeDetail();
      return data;
    },
    retry: false,
  });
}

export function useActivity() {
  return useQuery({
    queryKey: ["activity"],
    queryFn: async () => {
      const { data } = await getActivity();
      return data ?? [];
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { firstName: string; lastName: string }) => {
      await updateProfile({
        body: { first_name: vars.firstName, last_name: vars.lastName },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (vars: { currentPassword: string; newPassword: string }) => {
      await changePassword({
        body: { current_password: vars.currentPassword, new_password: vars.newPassword },
      });
    },
  });
}

// ─── Admin ───────────────────────────────────────────────────────────────

export function useAdminUsers() {
  return useQuery({
    queryKey: ["admin", "users"],
    queryFn: async () => {
      const { data } = await adminListUsers();
      return data ?? [];
    },
  });
}

export function useAdminUser(userId: string | null) {
  return useQuery({
    queryKey: ["admin", "users", userId],
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      const { data } = await adminGetUser({ path: { user_id: userId } });
      return data;
    },
    enabled: !!userId,
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      userId: string;
      firstName?: string;
      lastName?: string;
      role?: Role;
    }) => {
      await adminUpdateUser({
        path: { user_id: vars.userId },
        body: {
          first_name: vars.firstName,
          last_name: vars.lastName,
          role: vars.role,
        },
      });
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "users", vars.userId] });
    },
  });
}

/** Deactivate (`disabled: true`) or reactivate a user; either ends their live sessions. */
export function useSetUserStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string; disabled: boolean }) => {
      const { data } = await adminSetUserStatus({
        path: { user_id: vars.userId },
        body: { disabled: vars.disabled },
      });
      return data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "users", vars.userId] });
      // The change is written to the activity log in the same transaction.
      void queryClient.invalidateQueries({ queryKey: ["admin", "activity"] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      await adminDeleteUser({ path: { user_id: vars.userId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

// ─── Admin workspaces ────────────────────────────────────────────────────

export function useAdminWorkspaces(page: number) {
  return useQuery({
    queryKey: ["admin", "workspaces", { page }],
    queryFn: async () => {
      const { data } = await adminListWorkspaces({ query: { page, page_size: 25 } });
      return data;
    },
    placeholderData: (prev) => prev,
  });
}

/** A workspace with its engagements, assigned staff and client members. */
export function useAdminWorkspace(workspaceId: string) {
  return useQuery({
    queryKey: ["admin", "workspaces", workspaceId],
    queryFn: async () => {
      const { data } = await adminGetWorkspace({ path: { workspace_id: workspaceId } });
      return data;
    },
    retry: false,
  });
}

function useInvalidateWorkspaces() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: ["admin", "workspaces"] });
}

/** Create a workspace with its first engagement. */
export function useCreateWorkspace() {
  const onSuccess = useInvalidateWorkspaces();
  return useMutation({
    mutationFn: async (vars: { name: string; engagementName: string }) => {
      const { data } = await adminCreateWorkspace({
        body: { name: vars.name, engagement_name: vars.engagementName },
      });
      return data;
    },
    onSuccess,
  });
}

export function useAddWorkspaceMember(workspaceId: string) {
  const onSuccess = useInvalidateWorkspaces();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      const { data } = await adminAddWorkspaceMember({
        path: { workspace_id: workspaceId },
        body: { user_id: vars.userId },
      });
      return data;
    },
    onSuccess,
  });
}

export function useAssignStaff(engagementId: string) {
  const onSuccess = useInvalidateWorkspaces();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      const { data } = await adminAssignStaff({
        path: { engagement_id: engagementId },
        body: { user_id: vars.userId },
      });
      return data;
    },
    onSuccess,
  });
}

export function useUnassignStaff(engagementId: string) {
  const onSuccess = useInvalidateWorkspaces();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      await adminUnassignStaff({ path: { engagement_id: engagementId, user_id: vars.userId } });
    },
    onSuccess,
  });
}

// ─── Engagements and client invites (cockpit) ───────────────────────────

/** The engagements the caller may reach: assigned ones for staff, all for admins (first page). */
export function useEngagements() {
  return useQuery({
    queryKey: ["engagements"],
    queryFn: async () => {
      const { data } = await listEngagements({ query: { page_size: 100 } });
      return data;
    },
  });
}

export function useEngagement(engagementId: string) {
  return useQuery({
    queryKey: ["engagements", engagementId],
    queryFn: async () => {
      const { data } = await getEngagement({ path: { engagement_id: engagementId } });
      return data;
    },
    retry: false,
  });
}

/** Where the engagement stands: its phase, passed gates, settings and configuration pins. */
export function useEngagementState(engagementId: string) {
  return useQuery({
    queryKey: ["engagements", engagementId, "state"],
    queryFn: async () => {
      const { data } = await getEngagementState({ path: { engagement_id: engagementId } });
      return data;
    },
    retry: false,
  });
}

/** The phase and passed gates of the signed-in client's open engagement (portal). */
export function usePortalEngagementStage() {
  return useQuery({
    queryKey: ["portal", "engagement", "stage"],
    queryFn: async () => {
      const { data } = await getPortalEngagementStage();
      return data;
    },
    retry: false,
  });
}

/** The engagement's pending client invites, newest first (first page). */
export function useClientInvites(engagementId: string) {
  return useQuery({
    queryKey: ["engagements", engagementId, "invites"],
    queryFn: async () => {
      const { data } = await listClientInvites({
        path: { engagement_id: engagementId },
        query: { page_size: 100 },
      });
      return data;
    },
  });
}

export function useCreateClientInvite(engagementId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string }) => {
      const { data } = await createClientInvite({
        path: { engagement_id: engagementId },
        body: { email: vars.email },
      });
      return data;
    },
    // Settled, not only on success: an invite whose email failed is saved, unsent.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["engagements", engagementId, "invites"] });
    },
  });
}

/** Email a pending invite again; the earlier link stops working. */
export function useResendClientInvite(engagementId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { inviteId: string }) => {
      const { data } = await resendClientInvite({
        path: { engagement_id: engagementId, invite_id: vars.inviteId },
      });
      return data;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["engagements", engagementId, "invites"] });
    },
  });
}

// ─── People of an engagement ─────────────────────────────────────────────
// One set of hooks for both sides: without an engagement id they reach the client's own open
// engagement through the portal routes, with one the cockpit routes of that engagement.

function peopleKey(engagementId?: string) {
  return engagementId
    ? ["engagements", engagementId, "people"]
    : ["portal", "engagement", "people"];
}

/** The engagement's people (first page) and the variants its intake declares. */
export function usePeople(engagementId?: string) {
  return useQuery({
    queryKey: peopleKey(engagementId),
    queryFn: async () => {
      const query = { page_size: 100 };
      const { data } = engagementId
        ? await listEngagementPeople({ path: { engagement_id: engagementId }, query })
        : await listPortalPeople({ query });
      return data;
    },
    retry: false,
  });
}

/** Name a person, or replace a named person's fields (`personId`). Staff send a reason. */
export function useSavePerson(engagementId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { personId?: string; body: PersonFields | StaffPersonFields }) => {
      if (engagementId) {
        const body = vars.body as StaffPersonFields;
        const path = { engagement_id: engagementId };
        const { data } = vars.personId
          ? await updateEngagementPerson({ path: { ...path, person_id: vars.personId }, body })
          : await addEngagementPerson({ path, body });
        return data;
      }
      const { data } = vars.personId
        ? await updatePortalPerson({ path: { person_id: vars.personId }, body: vars.body })
        : await addPortalPerson({ body: vars.body });
      return data;
    },
    // Settled: naming an approver may have sent an invite even when the save answers an error.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: peopleKey(engagementId) });
      if (engagementId) {
        void queryClient.invalidateQueries({ queryKey: ["engagements", engagementId, "invites"] });
      }
    },
  });
}

/** Remove a person. Staff send a reason. */
export function useRemovePerson(engagementId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { personId: string; reason?: string }) => {
      if (engagementId) {
        await removeEngagementPerson({
          path: { engagement_id: engagementId, person_id: vars.personId },
          body: { reason: vars.reason ?? "" },
        });
        return;
      }
      await removePortalPerson({ path: { person_id: vars.personId } });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: peopleKey(engagementId) });
      if (engagementId) {
        void queryClient.invalidateQueries({ queryKey: ["engagements", engagementId, "invites"] });
      }
    },
  });
}

// ─── Admin activity ──────────────────────────────────────────────────────

export interface AdminActivityParams {
  search?: string;
  action?: string;
  userId?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export function useAdminActivity(params: AdminActivityParams = {}) {
  return useQuery({
    queryKey: ["admin", "activity", params],
    queryFn: async () => {
      const { data } = await adminListActivity({
        query: {
          search: params.search,
          action: params.action,
          user_id: params.userId,
          sort_by: params.sortBy ?? "created_at",
          sort_dir: params.sortDir ?? "desc",
          page: params.page ?? 1,
          page_size: params.pageSize ?? 25,
        },
      });
      return data;
    },
    // Keep the previous page on screen while fetching the next so the table
    // doesn't flash empty between paginations / filter changes.
    placeholderData: (prev) => prev,
  });
}

export function useAdminActivityAnalytics(days = 30) {
  return useQuery({
    queryKey: ["admin", "activity", "analytics", days],
    queryFn: async () => {
      const { data } = await adminActivityAnalytics({ query: { days } });
      return data;
    },
  });
}

export function useAdminActivityActions() {
  return useQuery({
    queryKey: ["admin", "activity", "actions"],
    queryFn: async () => {
      const { data } = await adminActivityActions();
      return data ?? [];
    },
  });
}

// ─── NDA ─────────────────────────────────────────────────────────────────

/** The NDA in force and whether the signed-in client accepted it (portal). */
export function usePortalNda() {
  return useQuery({
    queryKey: ["portal", "nda"],
    queryFn: async () => {
      const { data } = await getPortalNda();
      return data;
    },
  });
}

/**
 * Accept the NDA version the client read. Every portal query is invalidated, since the
 * gate held them all until now.
 */
export function useAcceptPortalNda() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { ndaVersionId: string; contentHash: string }) => {
      const { data } = await acceptPortalNda({
        body: { nda_version_id: vars.ndaVersionId, content_hash: vars.contentHash },
      });
      return data;
    },
    // Set before navigating on: the NDA guard reads this cache entry.
    onSuccess: (data) => {
      queryClient.setQueryData(["portal", "nda"], data);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["portal"] });
    },
  });
}

/** The NDA version in force, or null while none is published (admin). */
export function useAdminCurrentNda() {
  return useQuery({
    queryKey: ["admin", "nda"],
    queryFn: async () => {
      const { data } = await adminGetCurrentNda();
      return data ?? null;
    },
  });
}

/** Publish a new NDA version, in force at once (admin). */
export function useAdminPublishNda() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { versionLabel: string; content: string }) => {
      const { data } = await adminPublishNda({
        body: { version_label: vars.versionLabel, content: vars.content },
      });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "nda"] });
    },
  });
}

/** Who in the engagement's workspace accepted which NDA version, newest first (first page). */
export function useNdaAcceptances(engagementId: string) {
  return useQuery({
    queryKey: ["engagements", engagementId, "nda-acceptances"],
    queryFn: async () => {
      const { data } = await listNdaAcceptances({
        path: { engagement_id: engagementId },
        query: { page_size: 100 },
      });
      return data;
    },
  });
}

// ─── Portal preview (staff, read-only) ───────────────────────────────────

/**
 * The signed-in staff member's live portal preview, or `null` when there is none (404).
 * Pass `enabled: false` for clients, who never preview.
 */
export function usePortalPreview({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["portal", "preview"],
    queryFn: async () => {
      try {
        const { data } = await getPortalPreview();
        return data;
      } catch (error) {
        if (apiErrorStatus(error) === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled,
    retry: false,
  });
}

/** Start a read-only preview of an engagement's client portal (assigned staff, admins). */
export function useStartPortalPreview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (engagementId: string) => {
      const { data } = await startPortalPreview({ path: { engagement_id: engagementId } });
      return data;
    },
    onSuccess: (data) => {
      // The portal's cached reads may belong to another preview: drop them first.
      queryClient.removeQueries({ queryKey: ["portal"] });
      queryClient.setQueryData(["portal", "preview"], data);
    },
  });
}

/** Exit the portal preview; the staff session stays signed in. */
export function useEndPortalPreview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await endPortalPreview();
    },
    onSettled: () => {
      queryClient.removeQueries({ queryKey: ["portal"] });
    },
  });
}

/**
 * The live portal preview of the signed-in user: always `null` for a client, who never
 * previews. `isLoading` holds while a staff member's preview is still being read.
 */
export function usePreviewing() {
  const { data: me } = useMe();
  const staff = !!me && isStaffRole(me.role);
  const previewQuery = usePortalPreview({ enabled: staff });
  return {
    preview: staff ? (previewQuery.data ?? null) : null,
    isLoading: staff && previewQuery.isLoading,
  };
}

// ─── Intake ──────────────────────────────────────────────────────────────

/** Where the engagement's intake stands: the latest version, the freeze, category and pins. */
export function useEngagementIntake(engagementId: string) {
  return useQuery({
    queryKey: ["engagements", engagementId, "intake"],
    queryFn: async () => {
      const { data } = await getEngagementIntake({ path: { engagement_id: engagementId } });
      return data;
    },
    retry: false,
  });
}

/** Choose the engagement's process category, once. */
export function useSetProcessCategory(engagementId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (processCategory: string) => {
      const { data } = await setEngagementProcessCategory({
        path: { engagement_id: engagementId },
        body: { process_category: processCategory },
      });
      return data;
    },
    onSuccess: (state) => {
      queryClient.setQueryData(["engagements", engagementId, "intake"], state);
      void queryClient.invalidateQueries({ queryKey: ["engagements", engagementId, "state"] });
    },
  });
}

/** Save the intake form as the next draft version. */
export function useSaveIntakeDraft(engagementId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: IntakeDraftRequest) => {
      const { data } = await saveIntakeDraft({ path: { engagement_id: engagementId }, body });
      return data;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["engagements", engagementId, "intake"] });
    },
  });
}

/** Freeze an intake version: pins configuration and generates the data request and campaign. */
export function useFreezeIntake(engagementId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (intakeVersionId: string) => {
      const { data } = await freezeIntake({
        path: { engagement_id: engagementId },
        body: { intake_version_id: intakeVersionId },
      });
      return data;
    },
    // Settled: a refused freeze may follow a partial one, and the phase moves on success.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["engagements", engagementId] });
    },
  });
}

/** The engagement's latest data request, staff fields included; only once the intake is frozen. */
export function useEngagementDataRequest(engagementId: string, { enabled = true } = {}) {
  return useQuery({
    queryKey: ["engagements", engagementId, "data-request"],
    queryFn: async () => {
      const { data } = await getEngagementDataRequest({ path: { engagement_id: engagementId } });
      return data;
    },
    enabled,
    retry: false,
  });
}

/** Save the edited data request, with a reason, as the next released version. */
export function useSaveDataRequest(engagementId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SaveDataRequestRequest) => {
      const { data } = await saveDataRequestVersion({
        path: { engagement_id: engagementId },
        body,
      });
      return data;
    },
    onSuccess: (request) => {
      queryClient.setQueryData(["engagements", engagementId, "data-request"], request);
    },
    onError: () => {
      void queryClient.invalidateQueries({
        queryKey: ["engagements", engagementId, "data-request"],
      });
    },
    // Settled: a saved version changes the coverage rows and the version a row edit applies
    // to, and a refusal such as `version_outdated` means a newer one exists.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["engagements", engagementId, "coverage"] });
      void queryClient.invalidateQueries({ queryKey: draftFlowRunKey(engagementId) });
    },
  });
}

/** The client's engagement: the frozen intake and the released data request (portal). */
export function usePortalEngagementOverview() {
  return useQuery({
    queryKey: ["portal", "engagement", "overview"],
    queryFn: async () => {
      const { data } = await getPortalEngagementOverview();
      return data;
    },
    retry: false,
  });
}

// The draft-flow run's open items are the essential shelf-context rows' coverage, so whatever
// changes that coverage invalidates the run too (the hooks are under "Draft-flow run" below).
// The staff key stays outside the draft flow's own prefix: a run that turns done invalidates
// the draft flow, and that must not refetch the run again.
const PORTAL_DRAFT_FLOW_RUN_KEY = ["portal", "draft-flow", "run"];
const draftFlowRunKey = (engagementId: string) => ["engagements", engagementId, "draft-flow-run"];

// ─── Portal files ────────────────────────────────────────────────────────

// How often the shared files refetch while a job of any of them is queued or running.
const PORTAL_FILES_REFRESH_MS = 5_000;

/**
 * Every file the client shared in its open engagement, newest first, read page by page. While
 * a job of a file is at work, the list refetches every few seconds, so its outcome shows
 * unasked. A file parked until a model approval or the intake freeze is being read too, but
 * can wait for days, so it does not keep the list refetching.
 *
 * A file whose job settles, read or failed, changes the coverage with no action of the client,
 * and with it the draft-flow run's open items, so both are read again.
 */
export function usePortalFiles() {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["portal", "files"],
    queryFn: async () => {
      const cached = queryClient.getQueryData<PortalFile[]>(["portal", "files"]);
      const atWork = new Set(cached?.filter((f) => f.processing?.active).map((f) => f.id));
      const files = await readAllPortalFiles();
      if (files.some((file) => atWork.has(file.id) && !file.processing?.active)) {
        logger.debug("[portal-documents] a file settled: reading the coverage again");
        void queryClient.invalidateQueries({ queryKey: ["portal", "coverage"] });
        void queryClient.invalidateQueries({ queryKey: PORTAL_DRAFT_FLOW_RUN_KEY });
      }
      return files;
    },
    refetchInterval: (query) =>
      query.state.data?.some((file) => file.processing?.active) ? PORTAL_FILES_REFRESH_MS : false,
    retry: false,
  });
}

async function readAllPortalFiles(): Promise<PortalFile[]> {
  // Keyed by id: an upload landing mid-walk shifts the newest-first pages by one.
  const files = new Map<string, PortalFile>();
  for (let page = 1; ; page++) {
    const { data } = await listPortalFiles({ query: { page, page_size: 100 } });
    const items = data?.items ?? [];
    for (const file of items) {
      files.set(file.id, file);
    }
    if (items.length === 0 || files.size >= (data?.total ?? 0)) {
      return [...files.values()];
    }
  }
}

/** Share a file into one row of the released data request. */
export function useUploadPortalFile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { requestItemKey: string; file: File }) => {
      const { data } = await uploadPortalFile({
        body: { file: vars.file, request_item_key: vars.requestItemKey },
      });
      return data;
    },
    // Settled: a 502 can follow a store whose outcome is unknown, so the list may have changed.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["portal", "files"] });
      void queryClient.invalidateQueries({ queryKey: ["portal", "coverage"] });
      void queryClient.invalidateQueries({ queryKey: PORTAL_DRAFT_FLOW_RUN_KEY });
    },
  });
}

/**
 * The client says it does not have one requested item, with a reason (portal). The answer is
 * the new coverage; the overview is refetched so the folder shows the mark.
 */
export function useMarkPortalRowMissing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { requestItemKey: string; reason: string }) => {
      const { data } = await markPortalRowMissing({
        path: { request_item_key: vars.requestItemKey },
        body: { reason: vars.reason },
      });
      return data;
    },
    onSuccess: (coverage) => {
      queryClient.setQueryData(["portal", "coverage"], coverage);
      void queryClient.invalidateQueries({ queryKey: ["portal", "engagement", "overview"] });
      void queryClient.invalidateQueries({ queryKey: PORTAL_DRAFT_FLOW_RUN_KEY });
    },
  });
}

/** The client removes a file it shared by mistake (portal); the files and coverage refetch. */
export function useRemovePortalFile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (fileId: string) => {
      await removePortalFile({ path: { file_id: fileId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["portal", "files"] });
      void queryClient.invalidateQueries({ queryKey: ["portal", "coverage"] });
      void queryClient.invalidateQueries({ queryKey: PORTAL_DRAFT_FLOW_RUN_KEY });
    },
  });
}

/** The client keeps a file the scan flagged as it is (portal); it is released and counted. */
export function useKeepPortalFile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (fileId: string) => {
      const { data } = await keepPortalFile({ path: { file_id: fileId } });
      return data;
    },
    // Settled: a 409 means the file was already decided, so the list is stale either way.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["portal", "files"] });
      void queryClient.invalidateQueries({ queryKey: ["portal", "coverage"] });
      void queryClient.invalidateQueries({ queryKey: PORTAL_DRAFT_FLOW_RUN_KEY });
    },
  });
}

/**
 * The client replaces a flagged file with a cleaned version in the same row (portal). The new
 * file is scanned like any upload, and the flagged one is removed.
 */
export function useReplacePortalFile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { fileId: string; file: File }) => {
      const { data } = await replacePortalFile({
        path: { file_id: vars.fileId },
        body: { file: vars.file },
      });
      return data;
    },
    // Settled: as for an upload, a 502 can follow a store whose outcome is unknown.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["portal", "files"] });
      void queryClient.invalidateQueries({ queryKey: ["portal", "coverage"] });
      void queryClient.invalidateQueries({ queryKey: PORTAL_DRAFT_FLOW_RUN_KEY });
    },
  });
}

// ─── Coverage ────────────────────────────────────────────────────────────

/** How complete the client's open engagement is: the five sources, gap requests, shelf (portal). */
export function usePortalCoverage() {
  return useQuery({
    queryKey: ["portal", "coverage"],
    queryFn: async () => {
      const { data } = await getPortalCoverage();
      return data;
    },
    retry: false,
  });
}

/** The engagement's coverage with its row table and the shelf-context status (cockpit). */
export function useEngagementCoverage(engagementId: string) {
  return useQuery({
    queryKey: ["engagements", engagementId, "coverage"],
    queryFn: async () => {
      const { data } = await getEngagementCoverage({ path: { engagement_id: engagementId } });
      return data;
    },
    retry: false,
  });
}

/**
 * The returned coverage replaces the cached one. It carries the shelf-context status, which the
 * engagement state shows too, and the rows the draft-flow run's open items follow.
 */
function useStoreEngagementCoverage(engagementId: string) {
  const queryClient = useQueryClient();
  return (coverage: EngagementCoverage | undefined) => {
    queryClient.setQueryData(["engagements", engagementId, "coverage"], coverage);
    void queryClient.invalidateQueries({ queryKey: ["engagements", engagementId, "state"] });
    void queryClient.invalidateQueries({ queryKey: draftFlowRunKey(engagementId) });
  };
}

/** Set one row's gap note or status, with a reason; saved as the next data-request version. */
export function useUpdateCoverageRow(engagementId: string) {
  const queryClient = useQueryClient();
  const onSuccess = useStoreEngagementCoverage(engagementId);
  return useMutation({
    mutationFn: async (vars: { requestItemKey: string; body: CoverageRowUpdate }) => {
      logger.debug("[coverage] updating a row:", vars.requestItemKey);
      const { data } = await updateCoverageRow({
        path: { engagement_id: engagementId, request_item_key: vars.requestItemKey },
        body: vars.body,
      });
      return data;
    },
    onSuccess,
    // A refusal such as `version_outdated` means the cached coverage is behind: read it again.
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: ["engagements", engagementId, "coverage"] });
      void queryClient.invalidateQueries({ queryKey: draftFlowRunKey(engagementId) });
    },
    // Either way the data request may have a newer version, which the intake editor saves on.
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ["engagements", engagementId, "data-request"],
      });
    },
  });
}

/** Mark the shelf context in or confirmed missing, with a reason; this opens the interviews. */
export function useSetShelfContextStatus(engagementId: string) {
  const onSuccess = useStoreEngagementCoverage(engagementId);
  return useMutation({
    mutationFn: async (body: ShelfContextStatusUpdate) => {
      logger.debug("[coverage] setting the shelf context:", body.status);
      const { data } = await setShelfContextStatus({
        path: { engagement_id: engagementId },
        body,
      });
      return data;
    },
    onSuccess,
  });
}

// ─── Engagement library (cockpit) ────────────────────────────────────────

export type EngagementFilesParams = NonNullable<ListEngagementFilesData["query"]>;

/** One page of the engagement's files in one view (library, quarantine or decided), filtered. */
export function useEngagementFiles(engagementId: string, params: EngagementFilesParams) {
  return useQuery({
    queryKey: ["engagements", engagementId, "files", params],
    queryFn: async () => {
      const { data } = await listEngagementFiles({
        path: { engagement_id: engagementId },
        query: params,
      });
      return data;
    },
    retry: false,
  });
}

const ingestBackfillKey = (engagementId: string) => [
  "engagements",
  engagementId,
  "ingest-backfill",
];

// A file added lands in one of the library's views and changes the coverage counts; one queued
// to be read again changes its row, and the draft-flow run waits on it. Either can change the
// backfill count: an added file whose ingestion fails to queue is one more to read again.
function useInvalidateEngagementFiles(engagementId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ["engagements", engagementId, "files"] });
    void queryClient.invalidateQueries({ queryKey: ["engagements", engagementId, "coverage"] });
    void queryClient.invalidateQueries({ queryKey: ingestBackfillKey(engagementId) });
    void queryClient.invalidateQueries({ queryKey: draftFlowRunKey(engagementId) });
  };
}

/** Add a file to the engagement, with a reason; evidence answers one row of the data request. */
export function useUploadEngagementFile(engagementId: string) {
  const onSettled = useInvalidateEngagementFiles(engagementId);
  return useMutation({
    mutationFn: async (body: EngagementFileUpload) => {
      logger.debug("[library] adding a file:", body.purpose);
      const { data } = await uploadEngagementFile({ path: { engagement_id: engagementId }, body });
      return data;
    },
    // Settled: a 502 can follow a store whose outcome is unknown, so the list may have changed.
    onSettled,
  });
}

/** How many of the engagement's files a backfill would queue to be read again now. */
export function useIngestBackfill(engagementId: string) {
  return useQuery({
    queryKey: ingestBackfillKey(engagementId),
    queryFn: async () => {
      const { data } = await getIngestBackfill({ path: { engagement_id: engagementId } });
      return data;
    },
    retry: false,
  });
}

/** Queue again the files that could not be read because of a problem on BOI's side. */
export function useRunIngestBackfill(engagementId: string) {
  const onSettled = useInvalidateEngagementFiles(engagementId);
  return useMutation({
    mutationFn: async () => {
      logger.debug("[library] running the ingest backfill");
      const { data } = await runIngestBackfill({ path: { engagement_id: engagementId } });
      return data;
    },
    // Settled: a failed run may have queued some files before it stopped.
    onSettled,
  });
}

// ─── Draft flow and contributor maps ─────────────────────────────────────

const draftFlowKey = (engagementId: string) => ["engagements", engagementId, "draft-flow"];
const mapKey = (engagementId: string, sessionId: string) => [
  "engagements",
  engagementId,
  "sessions",
  sessionId,
  "map",
];

/** The latest draft flow with its steps, the released version, and one page of versions. */
export function useDraftFlow(engagementId: string, page = 1) {
  return useQuery({
    queryKey: [...draftFlowKey(engagementId), page],
    queryFn: async () => {
      const { data } = await getDraftFlow({
        path: { engagement_id: engagementId },
        query: { page },
      });
      return data;
    },
    // A page change keeps the shown page until the next arrives, so the editor stays mounted.
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/** Save the edited draft flow as the next draft version, with a reason. */
export function useSaveDraftFlowVersion(engagementId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SaveFlowVersionRequest) => {
      logger.debug("[flows] saving the draft flow:", body.body.steps.length, "steps");
      const { data } = await saveDraftFlowVersion({
        path: { engagement_id: engagementId },
        body,
      });
      return data;
    },
    // Settled: a `version_outdated` means the cached view is behind too. A first saved version
    // turns the draft-flow run done, so the run is read again as well.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: draftFlowRunKey(engagementId) });
      return queryClient.invalidateQueries({ queryKey: draftFlowKey(engagementId) });
    },
  });
}

/** Release a draft-flow version; the released one before it becomes superseded. */
export function useReleaseDraftFlowVersion(engagementId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (versionId: string) => {
      logger.debug("[flows] releasing a draft-flow version:", versionId);
      const { data } = await releaseDraftFlowVersion({
        path: { engagement_id: engagementId, version_id: versionId },
      });
      return data;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: draftFlowKey(engagementId) }),
  });
}

/** One content version of the engagement with its body, to open an earlier flow read-only. */
export function useArtifactVersion(engagementId: string, versionId: string | null) {
  return useQuery({
    queryKey: ["engagements", engagementId, "artifacts", versionId],
    queryFn: async () => {
      const { data } = await getArtifactVersion({
        path: { engagement_id: engagementId, version_id: versionId ?? "" },
      });
      return data;
    },
    enabled: versionId !== null,
    retry: false,
  });
}

/** A session's latest contributor map with its steps, a page of versions, and the session. */
export function useContributorMap(engagementId: string, sessionId: string, page = 1) {
  return useQuery({
    queryKey: [...mapKey(engagementId, sessionId), page],
    queryFn: async () => {
      const { data } = await getContributorMap({
        path: { engagement_id: engagementId, session_id: sessionId },
        query: { page },
      });
      return data;
    },
    // A page change keeps the shown page until the next arrives, so the editor stays mounted.
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/** Save the edited contributor map as the next draft version, with a reason. */
export function useSaveContributorMapVersion(engagementId: string, sessionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SaveFlowVersionRequest) => {
      logger.debug("[flows] saving a contributor map:", body.body.steps.length, "steps");
      const { data } = await saveContributorMapVersion({
        path: { engagement_id: engagementId, session_id: sessionId },
        body,
      });
      return data;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: mapKey(engagementId, sessionId) }),
  });
}

/** Record a hand-run contributor's confirmation: the latest map freezes, the session submits. */
export function useConfirmContributorMap(engagementId: string, sessionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: ConfirmMapRequest) => {
      logger.debug("[flows] confirming a contributor map:", body.version_id);
      const { data } = await confirmContributorMap({
        path: { engagement_id: engagementId, session_id: sessionId },
        body,
      });
      return data;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: mapKey(engagementId, sessionId) }),
  });
}

// ─── Draft-flow run ──────────────────────────────────────────────────────

// How often the run refetches while its documents are read or its draft is built.
const DRAFT_FLOW_RUN_REFRESH_MS = 5_000;
// The client's run reads `reading` while it waits for a model approval too, which can last
// days: after this long reading, it refetches only once a minute.
const PORTAL_RUN_BACKOFF_AFTER_MS = 10 * 60_000;
const PORTAL_RUN_SLOW_REFRESH_MS = 60_000;

/**
 * Reading and building move on by themselves, so the run refetches while in either. A run
 * waiting for a model approval can wait for days, so it does not keep refetching.
 */
function draftFlowRunRefetchInterval(state: DraftFlowRun["state"] | undefined) {
  return state === "reading" || state === "building" ? DRAFT_FLOW_RUN_REFRESH_MS : false;
}

// When each portal run query was first seen reading, for the back-off below.
const portalRunReadingSince = new WeakMap<object, number>();

/** The client's run refetches while reading: often at first, then once a minute. */
function portalDraftFlowRunRefetchInterval(query: {
  state: { data?: PortalDraftFlowRun; dataUpdatedAt: number };
}) {
  if (query.state.data?.state !== "reading") {
    portalRunReadingSince.delete(query);
    return false;
  }
  const since = portalRunReadingSince.get(query) ?? query.state.dataUpdatedAt;
  portalRunReadingSince.set(query, since);
  return Date.now() - since < PORTAL_RUN_BACKOFF_AFTER_MS
    ? DRAFT_FLOW_RUN_REFRESH_MS
    : PORTAL_RUN_SLOW_REFRESH_MS;
}

/**
 * A start refused because essential items are still open names them: they become the cached
 * run's open items at once, and the refetch that follows the refusal reads them again.
 */
function cacheRefusedOpenItems(queryClient: QueryClient, queryKey: string[], error: unknown) {
  if (apiErrorDetail(error) !== "essential_items_open") {
    return;
  }
  queryClient.setQueryData<{ open_items: string[] }>(
    queryKey,
    (run) => run && { ...run, open_items: apiErrorRefusal(error).ids },
  );
}

/** Where the client's start of the draft flow stands, and the essential items still open (portal). */
export function usePortalDraftFlowRun() {
  return useQuery({
    queryKey: PORTAL_DRAFT_FLOW_RUN_KEY,
    queryFn: async () => {
      const { data } = await getPortalDraftFlowRun();
      return data;
    },
    refetchInterval: (query) => portalDraftFlowRunRefetchInterval(query),
    retry: false,
  });
}

/** The client starts the draft flow: its documents are on BOI's side (portal). */
export function useStartPortalDraftFlowRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      logger.debug("[draft-flow-run] starting from the portal");
      const { data } = await startPortalDraftFlowRun();
      return data;
    },
    onSuccess: (run) => {
      queryClient.setQueryData(PORTAL_DRAFT_FLOW_RUN_KEY, run);
    },
    // A refusal means the cached run is behind: an item reopened, or a run already started.
    onError: (error) => {
      cacheRefusedOpenItems(queryClient, PORTAL_DRAFT_FLOW_RUN_KEY, error);
      void queryClient.invalidateQueries({ queryKey: PORTAL_DRAFT_FLOW_RUN_KEY });
    },
  });
}

/**
 * The engagement's draft-flow run: its state, the latest run and the open items (cockpit). A run
 * seen in another state that reads done has just written the draft flow, so the draft flow is
 * read again; a run that was already done when first read changes nothing there.
 */
export function useDraftFlowRun(engagementId: string) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: draftFlowRunKey(engagementId),
    queryFn: async () => {
      const seen = queryClient.getQueryData<DraftFlowRun>(draftFlowRunKey(engagementId))?.state;
      const { data } = await getDraftFlowRun({ path: { engagement_id: engagementId } });
      if (seen !== undefined && seen !== "done" && data?.state === "done") {
        logger.debug("[draft-flow-run] done: reading the draft flow again");
        void queryClient.invalidateQueries({ queryKey: draftFlowKey(engagementId) });
      }
      return data;
    },
    refetchInterval: (query) => draftFlowRunRefetchInterval(query.state.data?.state),
    retry: false,
  });
}

/** Staff start the draft flow for the client, or start it again after a failed run. */
export function useStartDraftFlowRun(engagementId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      logger.debug("[draft-flow-run] starting for the client");
      const { data } = await startDraftFlowRun({ path: { engagement_id: engagementId } });
      return data;
    },
    onSuccess: (run) => {
      queryClient.setQueryData(draftFlowRunKey(engagementId), run);
    },
    // A refusal means the cached run is behind: an item reopened, or a run already started.
    onError: (error) => {
      cacheRefusedOpenItems(queryClient, draftFlowRunKey(engagementId), error);
      void queryClient.invalidateQueries({ queryKey: draftFlowRunKey(engagementId) });
    },
  });
}
